import _ from 'lodash'

import { UI2IconName } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import { isUI2IconName } from 'shared-libs/components/ui2/icons/ui2-icon-registry'
import { Entity } from 'shared-libs/models/entity'

/**
 * Webapp-side resolution of the display info an entity declares in its JSON
 * schema.
 *
 * The schema's `metadata.entityDisplayColor` / `metadata.entityDisplayIcon`
 * fields carry the platform-agnostic display info (see e.g.
 * `schemas/entities/yms/trailer.json`). The icon name is validated against the
 * UI2 icon vocabulary and returned as a name, not a component — callers render
 * it through `UI2Icon`. This module and its mobile twin used to keep separate
 * three-entry allowlists that had to be extended in lockstep.
 */

// Fallback stripe color when an entity declares only entityDisplayIcon and
// no color. A muted grey keeps the stripe legible without asserting a brand
// identity for the entity type.
const DEFAULT_STRIPE_COLOR = '#8A8F99'

export interface WebEntityDisplayInfo {
  color: string
  iconName: UI2IconName
}

/**
 * Returns the info needed to render the entity-preview-card's type-stripe, or
 * `undefined` if the entity opts out of the stripe.
 *
 * Rules (mirrors mobile — see `EntityDisplayRegistry.ts`):
 *   • no `entityDisplayIcon` → no stripe (returns undefined).
 *     A color alone can't render a legible stripe, so color-without-icon is
 *     treated as "no stripe" rather than "empty colored bar".
 *   • `entityDisplayIcon` present but outside the UI2 icon vocabulary → no stripe.
 *   • `entityDisplayIcon` present, `entityDisplayColor` missing →
 *     stripe rendered with `DEFAULT_STRIPE_COLOR`.
 *   • both present → stripe rendered in the configured color.
 */
export function getWebEntityDisplay(
  entity: Entity | undefined | null
): WebEntityDisplayInfo | undefined {
  return getWebEntityDisplayFromSchema(_.last(entity?.schemas))
}

/**
 * Narrows a caller-supplied icon name to the UI2 vocabulary, returning
 * `undefined` when it is unset or unknown. Used by components that receive
 * `iconName` from their own props rather than from the entity schema.
 */
export function getWebEntityIconByName(iconName: string | undefined): UI2IconName | undefined {
  return isUI2IconName(iconName) ? iconName : undefined
}

/**
 * Schema-based variant for callers that don't yet have a materialized entity
 * (e.g. the entity-search field's empty-state button: no selection has been
 * made, but we still want to render the target entity type's icon).
 */
export function getWebEntityDisplayFromSchema(
  schema: any
): WebEntityDisplayInfo | undefined {
  const iconName: string | undefined = _.get(schema, 'metadata.entityDisplayIcon')
  if (!isUI2IconName(iconName)) {
    return undefined
  }
  const color: string | undefined = _.get(schema, 'metadata.entityDisplayColor')
  return {
    color: color ?? DEFAULT_STRIPE_COLOR,
    iconName,
  }
}
